import { Form, Modal, Select, message } from 'antd';
import React, { FC, useCallback } from 'react';
import { connect } from 'react-redux';
import { stockLocationsRestCrudThunksForSupplier } from '../../thunks/stock_locations_thunks';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { allowFieldTechToManageStockLocationsForSupplier } from '../../thunks/field_techs_thunks';
import SelectWithAllToggle from '../common/SelectWithAllToggle';

const TARGET_COLLECTION = 'fieldTechsDropdown';

const EditManageStockLocationsModal: FC<any> = ({
	onCancel,
	onSuccess,
	fieldTech,
	stockLocations,
	fetchStockLocations,
	fetchMultipleStockLocations,
	updateStockLocations,
	updating,
}): React.ReactElement => {
	const [form] = Form.useForm();

	const handleSubmit = useCallback(
		(values) => {
			const canManageAllStockLocations = !!values.canManageAllStockLocations;
			return updateStockLocations({
				email: nullSafeGet('email', fieldTech),
				canManageAllStockLocations,
				stockLocationIds: canManageAllStockLocations
					? []
					: nullSafeGetOrElse('stockLocationIds', values, []),
			})
				.then(() => onSuccess && onSuccess())
				.catch(() => message.error('Unable to update managed stock locations.'));
		},
		[fieldTech, onSuccess, updateStockLocations]
	);

	return (
		<Modal
			visible={true}
			width={600}
			title={'Edit Managed Stock Locations'}
			okText={'Save'}
			onCancel={onCancel}
			onOk={form.submit}
			closable={true}
			confirmLoading={updating}
			destroyOnClose={true}
		>
			<Form
				form={form}
				layout="vertical"
				className="supplierForm"
				preserve={false}
				initialValues={{
					canManageAllStockLocations: !!nullSafeGet('canManageAllStockLocations', fieldTech),
					stockLocationIds: nullSafeGetOrElse('managedStockLocations', fieldTech, []).map(
						(stockLocation) => stockLocation.id
					),
				}}
				onFinish={handleSubmit}
			>
				<SelectWithAllToggle
					label={'Stock Locations'}
					name={'stockLocationIds'}
					allName={'canManageAllStockLocations'}
					form={form}
					selectAllPlaceholder={'All stock locations'}
				>
					<OWAsyncSelect
						mode="multiple"
						stateSlice={stockLocations}
						targetCollectionName={TARGET_COLLECTION}
						fetchMultiple={(ids, targetCollectionName) => {
							fetchMultipleStockLocations(ids, targetCollectionName);
						}}
						fetchData={(
							searchText,
							targetCollectionName,
							pagination,
							sorting,
							filters,
							addToTargetCollection
						) => {
							fetchStockLocations(
								{ name: searchText },
								targetCollectionName,
								pagination,
								sorting,
								filters,
								addToTargetCollection
							);
						}}
						renderRecord={(stockLocation) => (
							<Select.Option value={stockLocation.id} key={stockLocation.id}>
								{stockLocation.name}
							</Select.Option>
						)}
						sortBy={{ sort_by: 'name', order: 'ascend' }}
					/>
				</SelectWithAllToggle>
			</Form>
		</Modal>
	);
};

const mapStateToProps = (state) => ({
	stockLocations: state.stock_locations,
	updating: state.field_techs.updating,
	fieldTech: state.field_techs.detail,
});

const mapDispatchToProps = (dispatch) => ({
	fetchMultipleStockLocations: (ids, targetCollectionName) =>
		dispatch(stockLocationsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
	fetchStockLocations: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			stockLocationsRestCrudThunksForSupplier.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	updateStockLocations: (entity) =>
		dispatch(allowFieldTechToManageStockLocationsForSupplier(entity)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EditManageStockLocationsModal);
